import { Component, OnDestroy, OnInit } from '@angular/core';
import { DateTimeControl } from 'app/shared/dynamic-forms/form-models/datetime-control';
import { FormControlBase, TextBoxControl } from 'app/shared/dynamic-forms/form-models/form-control-base';
import { PrimeDropdownControl } from 'app/shared/dynamic-forms/form-models/prime-dropdown-control';
import { DropdownItem } from 'app/viewmodels/dropdown-item';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ObjectDrawerFilter } from '../object-drawer-filter/object-drawer-filter';
import { ObjectDrawerFilterValueType } from '../object-drawer-filter/object-drawer-filter-value-type';
import { ObjectDrawerService } from '../object-drawer.service';

@Component({
  selector: 'hb-object-drawer-filter-dialog',
  templateUrl: './object-drawer-filter-dialog.component.html',
})
export class ObjectDrawerFilterDialogComponent implements OnInit, OnDestroy {
  private unsubscribe = new Subject<boolean>();
  public availableFilters: ObjectDrawerFilter[] = [];
  controls: FormControlBase<any>[] = [];

  private readonly booleanTrueKey = 'true';
  private readonly booleanFalseKey = 'false';

  constructor(private drawerService: ObjectDrawerService) {}

  ngOnInit() {
    const observables = this.drawerService.getFilterOptionsObservables(this.availableFilters);
    if (Object.keys(observables).length > 0 && observables.constructor === Object) {
      this.drawerService
        .getLazyLoadFilterOptions(observables)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((result: any) => {
          this.updateFilterOptions(result);
          this.setControls();
        });
    } else {
      this.setControls();
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  private updateFilterOptions(result: any): void {
    if (result && Object.entries(result).length > 0) {
      const entries: any = Object.entries(result);
      entries.forEach(e => {
        const filter = this.availableFilters.find(f => f.propertyName === e[0]);
        if (filter) {
          filter.options = e[1];
          filter.isFromSearchInput = false;
        }
      });
    }
  }

  okClicked(): void {
    this.setFiltersValueAndApply();
  }

  private setFiltersValueAndApply(): void {
    if (this.controls && this.controls.length > 0) {
      let searchRequired = false;
      this.controls.forEach(e => {
        if (e.value) {
          const foundFilter = this.findObjectDrawerFilter(this.availableFilters, e.propertyName);
          if (foundFilter) {
            this.setFilterValue(foundFilter, e.value);
            this.drawerService.addFilter(foundFilter);
            searchRequired = true;
          }
        } else {
          const foundAppliedFilter = this.findObjectDrawerFilter(this.drawerService.appliedFilters, e.propertyName);
          if (foundAppliedFilter) {
            this.drawerService.removeFilter(foundAppliedFilter);
            searchRequired = true;
          }
        }
      });

      if (searchRequired) {
        this.drawerService.filtersApplied.next();
      }
    }
  }

  private setFilterValue(filter: ObjectDrawerFilter, value: any): void {
    if (filter.valueType === ObjectDrawerFilterValueType.Enum) {
      filter.value = this.getEnumFilterValue(filter, Number(value));
    } else if (filter.valueType === ObjectDrawerFilterValueType.String && filter.lazyLoad && filter.lazyLoadEndpoint) {
      filter.value = this.getEnumFilterValue(filter, Number(value), false);
    } else if (filter.valueType === ObjectDrawerFilterValueType.Boolean) {
      filter.value = value === this.booleanTrueKey ? 'Yes' : 'No';
    } else {
      filter.value = value;
    }
  }

  private getEnumFilterValue(filter: ObjectDrawerFilter, value: any, useFriendlyOptions: boolean = true): string {
    const options = useFriendlyOptions ? filter.getUserFriendlyOptions() : filter.options;
    const found = options && options.length > 0 && options.find(e => e.key === value);
    return found && found.value;
  }

  private findObjectDrawerFilter(filters: ObjectDrawerFilter[], propertyName: string): ObjectDrawerFilter {
    return filters && filters.find(e => e.propertyName === propertyName);
  }

  private setControls(): void {
    this.controls = [];
    if (this.availableFilters && this.availableFilters.length > 0) {
      this.availableFilters.forEach(e => {
        switch (e.valueType) {
          case ObjectDrawerFilterValueType.String:
            if (e.lazyLoad && e.lazyLoadEndpoint) {
              const lazyLoadOptionsControl = this.createControl(e, PrimeDropdownControl, e.options);
              this.controls.push(lazyLoadOptionsControl);
            } else {
              const textBoxControl = this.createControl(e, TextBoxControl);
              this.controls.push(textBoxControl);
            }
            break;
          case ObjectDrawerFilterValueType.Boolean:
            const boolDropdownControl = this.createControl(e, PrimeDropdownControl, this.createBoolOptions());
            this.controls.push(boolDropdownControl);
            break;
          case ObjectDrawerFilterValueType.Date:
            const dateTimeControl = this.createControl(e, DateTimeControl);
            this.controls.push(dateTimeControl);
            break;
          case ObjectDrawerFilterValueType.Enum:
            const dropdownControl = this.createControl(e, PrimeDropdownControl, e.getUserFriendlyOptions());
            this.controls.push(dropdownControl);
            break;
        }
      });
    }
  }

  private createControl<T>(filter: ObjectDrawerFilter, controlType: new ({}) => T, options: any[] = null): T {
    return  new controlType({
      key: filter.propertyName,
      label: filter.displayName,
      propertyName: filter.propertyName,
      required: false,
      options: options && options.length > 0 ? options.map(e => new DropdownItem(e.value, e.key)) : [],
      defaultValue: this.getAppliedFilterValue(filter, options),
    });
  }

  private createBoolOptions(): any[] {
    return [
      { key: this.booleanFalseKey, value: 'No' },
      { key: this.booleanTrueKey, value: 'Yes' },
    ];
  }

  private getAppliedFilterValue(filter: ObjectDrawerFilter, options: any[] = null): any {
    const { appliedFilters } = this.drawerService;
    if (appliedFilters && appliedFilters.length > 0) {
      const found = appliedFilters.find(e => e.propertyName === filter.propertyName);
      if (found) {
        if (options) {
          const optionValue = options.find(e => e.value.toUpperCase() === found.value.toUpperCase());
          return (optionValue && optionValue.key) || null;
        } else {
          return found.valueType === ObjectDrawerFilterValueType.Date ? moment.utc(found.value,'YYYY-MM-DD HH:mm:ss').toISOString() : found.value;
        }
      }
      return null;
    }
    return null;
  }
}
